/**
 * Transição "macOS Spaces" — push horizontal entre páginas no fluxo
 * login ↔ dashboard. A página atual desliza pra fora (off-screen), a nova
 * entra deslizando do outro lado.
 *
 * Estratégia: helper aplica `gk-page-leaving-*` no body e seta
 * `html.dataset.pageEntering` com a classe que a próxima rota deve aplicar.
 * O `<PageTransitionManager>` no root layout escuta `usePathname` e, no
 * mount da nova rota, remove a leaving + aplica a entering por 800ms.
 *
 * Isso evita o "pisco" entre a página atual deslizar pra fora e a nova
 * montar — o body fica off-screen até a nova rota estar pronta.
 *
 *   await withSpacesTransition(() => router.push("/dashboard"), "forward");
 *   await withSpacesTransition(() => router.push("/login"),     "backward");
 */

const ANIMATION_MS = 800;

export type SpacesDirection = "forward" | "backward";

export async function withSpacesTransition(
  navigate: () => void,
  direction: SpacesDirection = "forward",
): Promise<void> {
  if (typeof document === "undefined") {
    navigate();
    return;
  }

  const body = document.body;
  const html = document.documentElement;
  const leaveCls =
    direction === "forward" ? "gk-page-leaving-left" : "gk-page-leaving-right";
  const enterCls =
    direction === "forward" ? "gk-page-entering-right" : "gk-page-entering-left";

  html.dataset.pageEntering = enterCls;
  body.classList.add(leaveCls);

  await new Promise<void>((resolve) => setTimeout(resolve, ANIMATION_MS));
  navigate();
  // Não remove leaveCls aqui — fica off-screen até a nova rota montar e o
  // PageTransitionManager fazer o swap pra enterCls.
}
